'use client';

/**
 * PushSubscriptionSync — headless component (renders null)
 *
 * Mounted globally in AppLayout so it runs on every authenticated session.
 *
 * Three responsibilities:
 * 1. Re-registers the service worker on every page load so push events are
 *    always handled (the SW is only initially registered during subscribe(),
 *    so without this, a lost registration means FCM delivers but the browser
 *    silently drops the push).
 * 2. Detects when the browser has a PushManager subscription that the backend
 *    doesn't know about (DB reset, cleanup cron, failed initial save) and
 *    re-registers it automatically.
 * 3. Forces a fresh browser subscription when re-syncing to break the stale-
 *    subscription cycle (backend removed a dead subscription → PushSubscriptionSync
 *    re-adds the same stale one → push fails again → repeat).
 */

import { useEffect, useRef } from 'react';
import { reduxLogger } from '@/core/utils/logger';
import {
  useGetVapidPublicKeyQuery,
  useGetPushSubscriptionsQuery,
  useSubscribeToPushMutation,
  type PushSubscriptionData,
} from '@/api/features/users';
import { usePushNotifications } from '@/features/notifications';

/** Extract device info from user agent (duplicated from hook to avoid coupling) */
function getDeviceInfo(): { deviceName: string; browser: string; operatingSystem: string } {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let operatingSystem = 'Unknown';

  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';

  if (ua.includes('Windows')) operatingSystem = 'Windows';
  else if (ua.includes('Mac')) operatingSystem = 'macOS';
  else if (ua.includes('Linux')) operatingSystem = 'Linux';
  else if (ua.includes('Android')) operatingSystem = 'Android';
  else if (/iOS|iPhone|iPad/.test(ua)) operatingSystem = 'iOS';

  return { deviceName: `${browser} on ${operatingSystem}`, browser, operatingSystem };
}

/** Convert VAPID base64url key to Uint8Array for pushManager.subscribe() */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function PushSubscriptionSync() {
  const pushNotifications = usePushNotifications();
  const { data: vapidKeyData } = useGetVapidPublicKeyQuery();
  const { data: pushSubscriptions } = useGetPushSubscriptionsQuery();
  const [subscribeToPush] = useSubscribeToPushMutation();
  const reconciled = useRef(false);
  const swRegistered = useRef(false);

  // Ensure the service worker is registered on every page load.
  useEffect(() => {
    if (swRegistered.current) return;
    if (!pushNotifications.isSupported) return;

    swRegistered.current = true;
    navigator.serviceWorker.register('/service-worker.js')
      .then((reg) => {
        reduxLogger.info('Service worker registered/verified', { scope: reg.scope });
      })
      .catch((error: unknown) => {
        reduxLogger.error('Service worker registration failed:', error);
      });
  }, [pushNotifications.isSupported]);

  // Reconcile browser subscription with backend DB.
  // When the browser has a subscription the backend doesn't know about, it may
  // be because the backend removed a stale/dead subscription. Re-adding the same
  // one would just fail again on the next push. Instead we force a FRESH browser
  // subscription (unsubscribe → re-subscribe) so the push service issues new
  // credentials, then register that fresh subscription with the backend.
  useEffect(() => {
    if (reconciled.current) return;
    if (!pushNotifications.isSubscribed || !pushNotifications.subscription) return;
    if (pushSubscriptions === undefined) return;

    const currentEndpoint = pushNotifications.subscription.endpoint;
    const isRegisteredInBackend = pushSubscriptions.some(
      (sub) => sub.endpoint === currentEndpoint,
    );

    reconciled.current = true;
    if (isRegisteredInBackend) return;

    // Need VAPID key to create a fresh subscription
    const vapidPublicKey = vapidKeyData?.publicKey;
    if (!vapidPublicKey) {
      reduxLogger.warn('Cannot refresh push subscription: VAPID key not available yet');
      // Allow retry on next render when VAPID key loads
      reconciled.current = false;
      return;
    }

    // Force a fresh subscription to break the stale-subscription cycle
    (async () => {
      try {
        // 1. Unsubscribe the current (potentially stale) browser subscription
        const currentSub = pushNotifications.subscription;
        if (currentSub) {
          await currentSub.unsubscribe();
          reduxLogger.info('Unsubscribed stale browser push subscription');
        }

        // 2. Re-subscribe to get a fresh subscription from the push service
        const registration = await navigator.serviceWorker.ready;
        const freshSub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as unknown as BufferSource,
        });

        // 3. Register the fresh subscription with the backend
        const json = freshSub.toJSON();
        const deviceInfo = getDeviceInfo();

        await subscribeToPush({
          endpoint: json.endpoint ?? '',
          keys: {
            p256dh: json.keys?.p256dh || '',
            auth: json.keys?.auth || '',
          },
          deviceInfo,
        } as PushSubscriptionData).unwrap();

        reduxLogger.info('Push subscription refreshed and re-synced with backend');
      } catch (error: unknown) {
        reduxLogger.error('Failed to refresh push subscription:', error);
      }
    })();
  }, [pushNotifications.isSubscribed, pushNotifications.subscription, pushNotifications.getSubscriptionJSON, pushSubscriptions, subscribeToPush, vapidKeyData]);

  return null;
}
